<script setup lang="ts">
import type { Component, HTMLAttributes } from 'vue'
import { computed, inject, ref } from 'vue'
import { RouteLocationRaw, RouterLink } from 'vue-router'
import { cn } from '@ds/lib/utils'
import { DSButton, DSTooltip, DSTooltipContent, DSTooltipTrigger } from '@ds'
import { SIDEBAR_COLLAPSED } from './const'

type Props = {
  icon?: Component
  label: string
  active?: boolean
  class?: HTMLAttributes['class']
  to?: RouteLocationRaw
  isMobile?: boolean
  tooltip?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  active: false,
  tooltip: true,
})

defineOptions({
  inheritAttrs: false,
})

const collapsed = inject(SIDEBAR_COLLAPSED, ref(false))

const rowClasses = computed(() => cn(
  'transition-colors duration-ds-fast ease-ds-standard',
  collapsed.value ? 'size-9.25' : `${props.isMobile ? 'h-11' : 'h-9.25'} px-2.5 gap-2.5 justify-start`,
  props.active
    ? 'font-semibold bg-white/15 hover:bg-white/10 text-primary-foreground'
    : 'font-medium text-primary-foreground/85 hover:bg-white/10 hover:text-primary-foreground',
  props.class,
))
</script>

<template>
  <DSTooltip v-if="collapsed && tooltip">
    <DSTooltipTrigger as-child>
      <DSButton
        :class="rowClasses"
        type="button"
        variant="ghost"
        size="icon-sm"
        v-bind="$attrs"
        :to
        :as="to ? RouterLink : undefined"
      >
        <component
          :is="icon"
          v-if="icon"
          class="size-4.5!"
          aria-hidden="true"
        />
        <slot
          name="badge"
          :collapsed="true"
        />
      </DSButton>
    </DSTooltipTrigger>
    <DSTooltipContent
      side="right"
      :side-offset="11"
    >
      {{ label }}
    </DSTooltipContent>
  </DSTooltip>
  <DSButton
    v-else
    :class="rowClasses"
    type="button"
    variant="ghost"
    :size="collapsed ? 'icon-sm' : 'sm'"
    v-bind="$attrs"
    :to
    :as="to ? RouterLink : undefined"
  >
    <component
      :is="icon"
      v-if="icon"
      class="size-4.5!"
      aria-hidden="true"
    />
    <span :class="collapsed ? 'sr-only' : undefined">
      {{ label }}
    </span>
    <slot
      name="badge"
      :collapsed
    />
    <slot v-if="!collapsed" />
  </DSButton>
</template>
